import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { resolveUrl } from 'src/core/constants/api.constants';
import { SystemNotificationReceivedPayload } from 'src/core/models/notification/system-notification-received.payload';
import { HubService } from './hub.service';

// TODO: verify against live SignalR/Swagger — unverified assumption. Not confirmed in this environment
// (no way to run/inspect the live backend); modeled after this repo's own `/api/hubs/agent-heartbeat`
// convention (see AgentHeartbeatService).
const NOTIFICATION_HUB_URL = resolveUrl('/api/hubs/notification');

/**
 * SignalR client for `Sestek.Abp.Notification`'s push hub. Unlike `AgentHeartbeatService` there is no
 * per-connection session/group to re-register after a reconnect — the server pushes to the authenticated
 * user's connection directly, and the underlying `HubConnection` instance (and its `.on()` handlers)
 * survives an automatic reconnect, so `onReconnected` only needs to signal callers to catch up on any
 * pushes that may have been missed while disconnected (see `NotificationStateService`).
 */
@Injectable({ providedIn: 'root' })
export class NotificationHubService extends HubService {
  private readonly systemNotificationReceivedSource = new Subject<SystemNotificationReceivedPayload>();
  private readonly hubReconnectedSource = new Subject<void>();

  /** Emits every `SystemNotificationReceived` push while connected. */
  readonly systemNotificationReceived$: Observable<SystemNotificationReceivedPayload> =
    this.systemNotificationReceivedSource.asObservable();
  /** Emits after the hub successfully reconnects, so callers can refresh state that may be stale. */
  readonly hubReconnected$: Observable<void> = this.hubReconnectedSource.asObservable();

  constructor() {
    super();
    this.initialize({ url: NOTIFICATION_HUB_URL });
  }

  async connect(): Promise<void> {
    await this.startConnection();
  }

  async disconnect(): Promise<void> {
    await this.stopConnection();
  }

  protected override async onConnected(): Promise<void> {
    this.hubConnection?.off('SystemNotificationReceived');
    this.hubConnection?.on('SystemNotificationReceived', (payload: SystemNotificationReceivedPayload) => {
      this.systemNotificationReceivedSource.next(payload ?? {});
    });
  }

  protected override async onReconnected(): Promise<void> {
    this.hubReconnectedSource.next();
  }
}
